<template>
    <div 
        :id="props.lineId"
        class=" line no-outline"
        tabindex="0"
        @focus="onFocus"
    >
        <h5 v-if="props.title && props.items && props.items.length > 0" class="line__title">{{ props.title }}</h5>
        <div
            ref="root"
            class="line__content"
            data-type="line-content"
        >
            <div 
                v-for="(item, index) in items"
                :key="calculateId(item)"
                class="line__card"
                :class="{ 'line__card_scrollable': isScrollable }"
                data-type="card"
            >
                <card 
                    :id="getId(index)"
                    :ref="setBaseCardRefs"
                    :card-data="item"
                    :data-rowId="props.lineId"
                    :data-keyleft="getPrevId(index)"
                    :data-keyright="getNextId(index)"
                    :index="index"
                    :component-name="item.getComponentName()"
                    :enable-program-info="props.enableProgramInfo"
                    @item-selected="onItemSelected"
                    @index-focused="onIndexFocused"
                    @card-focused="onCardFocused"
                    @program-info="onProgramInfo"
                    @focus="onFocusCard()"
                    @blur="onBlurCard()"
                />
            </div>
        </div>
        <div v-show="props.isLoadingNextPage" class="line-loader">
            <loader />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { watch, type PropType, inject, onMounted, ref, type Ref, computed, nextTick, onBeforeUpdate, type ComponentPublicInstance } from "vue";
    import { CardDataType, type CardData } from "@/components/Cards/types";
    import { useApplicationStore } from "@/stores/Application";
    import type { IEventSelected, OnKeyUpDown } from "./types";
    import { type Genre } from "@ca.vmedia.tv/domains";
    import { type shelfKeys as ShelfKeysType } from "@/common";
    import type { IProgramInfo } from "@/composables/useProgramInfo/types";

    const ApplicationStore = useApplicationStore();
    const { addSelectedIdProgram } = ApplicationStore;

    const props = defineProps({
        indexRow: {
            type: Number,
            required: true
        },
        /**
         * The items to display on this line. Should be derived from CardData class
         */
        items: {
            type: (null as unknown) as PropType<CardData[] | null>,
            required: true
        },
        /**
         * The line title (such as Channels, Recently Added Shows, etc)
         */
        title: {
            type: [String, null] as PropType<string | null>,
            required: false,
            default: null
        },
        /**
         * The line unique identifier for the navigation purposes
         */
        lineId: {
            type: String,
            required: true
        },
        keyData: {
            type: String,
            required: true
        },
        genre: {
            type: (null as unknown) as PropType<Genre | null>,
            required: false,
            default: null
        },
        /**
         * The flag to indicate if the next page is loading
         */
        isLoadingNextPage: {
            type: Boolean,
            required: false
        },
        /**
         * The flag to indicate if the program info should be enabled
         */
         enableProgramInfo: {
            type: Boolean,
            required: false,
            default: false
        },
        playItemId: {
            type: [Number, String, null] as PropType<number | string | null>,
            required: false,
            default: null
        },
    });

    const emit = defineEmits(['program-info', 'show-all', 'plus', 'list-item-selected', 'add-list-items',  'loaded-line', 'load-next-page', 'load-previous-page']);
    const { $navigator } = useNuxtApp();
    const focusedIndex: Ref<number> = ref(0);
    const isLoadedLine: Ref<boolean> = ref(false);
    const root: Ref<HTMLDivElement | null> = ref(null);
    const numberOfVisibleCards: number = 4;
    const isScrollable = computed((): boolean => props.items ? props.items.length > numberOfVisibleCards : false);
    const focusedCard = ref<CardData | null>(null);
    const focusedCardRect = ref<DOMRect | null>(null);
    
    const SHIFT_THRESHOLD_PX = 1; // Minimum pixel shift to adjust scroll

    const onKeyDown = inject<OnKeyUpDown>('onKeyDown');
    const onKeyUp = inject<OnKeyUpDown>('onKeyUp');
    
    const onFocusCard = () => {
        registerKeyListeners();
    };

    const onBlurCard = () => {
        unregisterKeyListeners();
    };

    let baseCardRefs: ComponentPublicInstance[] = [];
    const setBaseCardRefs = (el: Element | ComponentPublicInstance | null) => {
        if (el) {
            baseCardRefs.push(el as ComponentPublicInstance);
        }
    }

    const calculateId = (item: CardData) => item.getId();

    const _setLinePosition = (index: number) => {
        const position: ScrollLogicalPosition = props.enableProgramInfo ? "start" : "center";
        const selectedItem: ComponentPublicInstance = baseCardRefs[index];

        if (selectedItem != null) {
            const element: HTMLElement | null = selectedItem.$el?.parentElement?.parentElement?.parentElement;

            if (element) {
                element.scrollIntoView({block: position});
            }
        }
    };

    const getId = (index: number): string => {
        if (props.items && props.items.length != 0) {
            return `${props.lineId}_${(props.items[index])?.getId()}`;
        } else {
            return '';
        }
    };

    const getPrevId = (index: number) => {
        return index == 0 ? null : getId(index - 1);
    };

    const getNextId = (index: number) => {
        return props.items && (index < props.items.length - 1) ? getId(index + 1)  : null;
    };

    const onItemSelected = (item: CardData) => {
        const idElement = `${props.lineId}_${item.getId()}`;
        const isNotAdded = idElement.startsWith('player');
        
        if (!isNotAdded) {
            addSelectedIdProgram(idElement);
        }

        const itemType = item.getType();
        
        const selectedItem: IEventSelected = {
            item: item, 
            lineId: props.lineId as ShelfKeysType, 
            componentName: item.getComponentName(),
            genre: props.genre
        };
        
        if (itemType === CardDataType.ShowAll) {
            emit("show-all", selectedItem);
        } else if (itemType === CardDataType.Plus) {
            emit("plus", props.lineId);
        } else {
            emit("list-item-selected", selectedItem);
        }
    };
    const savePositionOfFocusedCard = () => {
        const id = `${props.lineId}_${focusedCard.value?.getId()}`;
        const element: HTMLElement | null = document.querySelector(`#${id}`);
        if (element) {
            focusedCardRect.value = element.getBoundingClientRect();
        }
    };

    const _emitLoadNextPage = (index: number) => {
        if (props.items && (index === props.items.length - 1)) {
            emit('load-next-page', props.keyData);
        }
    };
    
    const _emitLoadPreviousPage = (index: number) => {
        if (index === 0) {
            savePositionOfFocusedCard();
            emit('load-previous-page', props.keyData);
        }
    };
    
    const onIndexFocused = (index: number) => {
        focusedIndex.value = index;
        _emitLoadNextPage(index);
        _emitLoadPreviousPage(index);
    };
    
    const setFocused = (id: string) => {
        const element: HTMLElement | null = document.getElementById(id);
        if (element instanceof HTMLElement) {
            element.focus({preventScroll:true});
        }
    };

    const _setFirstFocused = (): void => {
        if (props.items && props.items.length > 0) {
            if (props.playItemId) {
                const idx = props.items.findIndex(item => item.getId() === String(props.playItemId));
                if (idx !== -1) {
                    focusedCard.value = props.items[idx];
                    setFocused(getId(idx));
                    return;
                }
            }
            setFocused(getId(0));
        }
    };

    const onFocus = () => {
        _setFirstFocused();
    };

    const registerKeyListeners = (): void => {
        if (!onKeyDown || !onKeyUp) {
            return;
        }
        $navigator.on("keypress:up", onKeyUp);
        $navigator.on("keypress:down", onKeyDown);
    };

    const unregisterKeyListeners = (): void => {
        if (!onKeyDown || !onKeyUp) {
            return;
        }
        $navigator.off("keypress:up", onKeyUp);
        $navigator.off("keypress:down", onKeyDown);
    };

    const onCardFocused = (card: CardData) => {
        _setLinePosition(focusedIndex.value);
        scrollHorizontally(card);
        focusedCard.value = card;
    };

    const onProgramInfo = (event: IProgramInfo): void => {
        emit('program-info', event);
    };

    const scrollRight = (card: HTMLElement) => {
        let rootElement = root.value;

        if (!rootElement) {
            rootElement = document.activeElement?.parentElement?.parentElement as HTMLDivElement;
        }

        const { right: cardRight, width: cardWidth } =
        card.getBoundingClientRect();

        let { right: rootRight } = rootElement.getBoundingClientRect();

        const style = window.getComputedStyle(rootElement);

        rootRight -= parseFloat(style.paddingLeft);
        let customPaddingLeftInRem = 0.4;
        const cardProgramWidthInRem = 17;
        if (Math.round(cardWidth) / (cardProgramWidthInRem * parseFloat(style.fontSize)) < 1) {
            customPaddingLeftInRem = 1.65;
        }
        rootRight -= parseFloat(String(customPaddingLeftInRem * parseFloat(style.fontSize)));
        
        if (cardRight <= rootRight) {
            return;
        }
        rootElement.scrollLeft += (cardRight - rootRight + cardWidth / 5);
    };

    const scrollLeft = (card: HTMLElement) => {
        let rootElement = root.value;
        if (!rootElement) {
            rootElement = document.activeElement?.parentElement?.parentElement as HTMLDivElement;
        }

        const { left: cardLeft, width: cardWidth } =
            card.getBoundingClientRect();
        let { left: rootLeft } = rootElement.getBoundingClientRect();
        const style = window.getComputedStyle(rootElement);
        rootLeft += parseFloat(style.paddingLeft);
        let customPaddingLeftInRem = 0.4;
        const cardProgramWidthInRem = 17;
        if (Math.round(cardWidth) / (cardProgramWidthInRem * parseFloat(style.fontSize)) < 1) {
            customPaddingLeftInRem = 1.65;
        }
        
        rootLeft += parseFloat(String(customPaddingLeftInRem * parseFloat(style.fontSize)));
        if (cardLeft >= rootLeft) {
            return;
        }
        rootElement.scrollLeft += (cardLeft - rootLeft - cardWidth / 5);
    };

    const scrollHorizontally = (card: CardData) => {
        const id = `${props.lineId}_${card.getId()}`;
        const element: HTMLElement | null = document.querySelector(`#${id}`);

        if (element) {
            scrollRight(element);
            scrollLeft(element);
        }
    };

    const emitLoadedLine = (items: CardData[] | null): void => {
        if (items !== null && Array.isArray(items) && isLoadedLine.value === false) {
            isLoadedLine.value = true;

            emit('loaded-line');
        }
    };
    const setFocusAfterUpdate = () => {
        let idElement = $navigator.getCurrentFocused()?.id;
        if (!idElement?.split('_').includes(props.lineId)) {
            return;
        }

        idElement = document.getElementById(idElement || '') ? idElement : getId(0);
        setFocused(idElement);
    };

    // Restore scroll position when items are added to the beginning
    const restoreCardsPositionAfterAddToBeginning = () => {
        const id = `${props.lineId}_${focusedCard.value?.getId()}`;
        const element: HTMLElement | null = document.querySelector(`#${id}`);
        const savedRect = focusedCardRect.value;
        
        if (element && root.value && savedRect) {
            const newCardRect = element.getBoundingClientRect();
            const rootRect = root.value.getBoundingClientRect();
            
            // Calculate how much the card shifted
            const shiftAmount = newCardRect.left - savedRect.left;
            
            // Clear the saved rect after restoration
            focusedCardRect.value = null;
            // Only adjust scroll if card shifted (items were prepended)
            // Positive shift means items were added to the beginning
            
            if (newCardRect.right <= rootRect.right) {
                return;
            }
            if (shiftAmount > SHIFT_THRESHOLD_PX) {
                root.value.scrollLeft += shiftAmount;
            }
        }
    };

    watch(
        () => props.items, 
        (value: CardData[] | null) => {
            emitLoadedLine(value);
        },
        { once: true }
    );

    watch(
        () => props.items?.length,
        (newLength, oldLength) => {
            // Only adjust scroll if we have a saved position (items were added to beginning)
            // If focusedCardRect is null, items were added to the end and no adjustment is needed
            if (isLoadedLine.value && newLength !== oldLength && focusedCard.value && focusedCardRect.value) {
                nextTick(() => {
                    restoreCardsPositionAfterAddToBeginning();
                });
            }
        }
    );

    onMounted(() => {
        nextTick(() => {
            emitLoadedLine(props.items);
        });
    });

    onBeforeUpdate(() => {
        baseCardRefs = []
    });
    onUpdated(() => {
        nextTick(() => {
            setFocusAfterUpdate();
        });
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>